'use client';

import { UPSCALE_MODELS } from '@app/api/upscale/_lib/upscaleSchemas';
import { type ChangeEvent, useCallback, useEffect, useMemo } from 'react';

import { type TextAreaState } from '@videoblocks/design-system';
import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { type VideoStockItemResult } from '../../_shared/_actions/searchLibraryVideos';
import { type Suggestion } from '../../_shared/_components/forms/TextAreaInputField/TextAreaInputField';
import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import {
    validateVideoEditForm,
    VIDEO_EDIT_FORM_DEFAULTS,
    type VideoEditFormErrors,
    type VideoEditFormState,
} from '../../_shared/_context/VideoEditFormState';
import {
    AI_TOOLKIT_TELEMETRY_ACTIONS,
    emitAiToolkitTag,
    toSuggestionAction,
    toUseCaseAction,
} from '../../_shared/_context/aiToolkitTelemetry';
import { MAX_PROMPT_LENGTH, VIDEO_EDIT_CREDIT_COST } from '../../_shared/_context/constants';
import {
    AI_TOOLKIT_CATEGORIES,
    AI_TOOLKIT_OPERATIONS,
    type AiTaskSubmitRouteResponse,
    type AiToolkitOperation,
} from '../../_shared/_context/types';
import { VIDEO_EDIT_USE_CASE_PLACEHOLDERS, VIDEO_EDIT_USE_CASE_SUGGESTIONS } from './constants';

const VIDEO_EDIT_ENDPOINT = '/api/video-edit';
const UPSCALE_ENDPOINT = '/api/upscale';

export type UseVideoEditFormReturn = {
    formState: VideoEditFormState;
    errors: VideoEditFormErrors;
    promptState: TextAreaState;
    promptHelperText: string | undefined;
    promptPlaceholder: string;
    suggestions: Suggestion[];
    isValid: boolean;
    isSubmitDisabled: boolean;
    submitError: string | null;
    calculatedCost: number;
    onPromptChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onPromptBlur: () => void;
    onUseCaseSelect: (id: string) => void;
    onSuggestionClick: (suggestion: Suggestion) => void;
    onVideoSelect: (item: VideoStockItemResult) => void;
    onVideoDeselect: () => void;
    onSubmit: () => void;
    onConfirm: (operation?: AiToolkitOperation) => Promise<void>;
};

export const useVideoEditForm = (): UseVideoEditFormReturn => {
    const {
        formStates,
        updateFormState,
        dispatchVideoEditSubmit,
        dispatchConfirmationModal,
        submitStates,
        refreshCredits,
        videoSearch,
        videoEditSubmitError: submitError,
        setVideoEditSubmitError: setSubmitError,
    } = useAiToolkitContext();
    const { setShowSearch } = videoSearch;
    const {
        status: videoEditStatus,
        completedTaskId,
        duration: editedDuration,
    } = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT];
    const isInProgress = videoEditStatus === 'inProgress';

    const formState = formStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT] ?? VIDEO_EDIT_FORM_DEFAULTS;
    const validationActive = formState.validationActive;

    useEffect(() => {
        if (!formStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]) {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, VIDEO_EDIT_FORM_DEFAULTS);
        }
    }, [formStates, updateFormState]);

    const { isValid, errors: schemaErrors } = useMemo(() => validateVideoEditForm(formState), [formState]);

    const isSubmitDisabled = !isValid;

    const errors: VideoEditFormErrors = useMemo(() => {
        if (!validationActive) {
            return {};
        }
        return { ...schemaErrors };
    }, [validationActive, schemaErrors]);

    const promptState: TextAreaState = useMemo(() => {
        if (validationActive && schemaErrors.prompt) {
            return 'error';
        }
        return 'default';
    }, [validationActive, schemaErrors.prompt]);

    const promptHelperText = useMemo((): string | undefined => {
        if (validationActive && schemaErrors.prompt) {
            return schemaErrors.prompt;
        }
        if (formState.prompt.length >= MAX_PROMPT_LENGTH) {
            return `You've reached the maximum allowed length (${MAX_PROMPT_LENGTH.toLocaleString()} characters).`;
        }
        return undefined;
    }, [validationActive, schemaErrors.prompt, formState.prompt.length]);

    const suggestions = useMemo(
        (): Suggestion[] => VIDEO_EDIT_USE_CASE_SUGGESTIONS[formState.useCase] ?? [],
        [formState.useCase],
    );

    const promptPlaceholder = useMemo(
        (): string => VIDEO_EDIT_USE_CASE_PLACEHOLDERS[formState.useCase] ?? '',
        [formState.useCase],
    );

    const updateField = useCallback(
        <K extends keyof VideoEditFormState>(field: K, value: VideoEditFormState[K]): void => {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, { ...formState, [field]: value });
        },
        [formState, updateFormState],
    );

    const onPromptChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>): void => {
            updateField('prompt', e.target.value);
        },
        [updateField],
    );

    const onPromptBlur = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, { ...formState, validationActive: true });
    }, [formState, updateFormState]);

    const onUseCaseSelect = useCallback(
        (id: string): void => {
            updateField('useCase', id);
            emitAiToolkitTag(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, toUseCaseAction(id));
        },
        [updateField],
    );

    const onSuggestionClick = useCallback(
        (suggestion: Suggestion): void => {
            updateField('prompt', suggestion.promptText);
            emitAiToolkitTag(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, toSuggestionAction(suggestion.label));
        },
        [updateField],
    );

    const onVideoSelect = useCallback(
        (item: VideoStockItemResult): void => {
            updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, {
                ...formState,
                sourceVideo: item.previewUrl,
                contentId: item.contentId,
            });
            dispatchVideoEditSubmit({
                type: 'SELECTION',
                originalVideoUrl: item.previewUrl,
                originalThumbnailUrl: item.thumbnailUrl,
                duration: item.duration,
                contentId: item.contentId,
                aspectRatio: item.aspectRatio,
            });
        },
        [formState, updateFormState, dispatchVideoEditSubmit],
    );

    const onVideoDeselect = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, {
            ...formState,
            sourceVideo: '',
            contentId: null,
        });
        dispatchVideoEditSubmit({ type: 'RESET' });
    }, [formState, updateFormState, dispatchVideoEditSubmit]);

    const onSubmit = useCallback((): void => {
        updateFormState(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, { ...formState, validationActive: true });

        if (!isValid) {
            return;
        }

        emitAiToolkitTag(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, AI_TOOLKIT_TELEMETRY_ACTIONS.generateVideo);
        dispatchConfirmationModal({
            type: 'OPEN_MODAL',
            modal: {
                type: 'creditConfirmation',
                category: AI_TOOLKIT_CATEGORIES.VIDEO_EDIT,
                operation: AI_TOOLKIT_OPERATIONS.GENERATE,
            },
        });
    }, [formState, updateFormState, isValid, dispatchConfirmationModal]);

    const onConfirm = useCallback(
        async (operation: AiToolkitOperation = AI_TOOLKIT_OPERATIONS.GENERATE): Promise<void> => {
            if (isInProgress) return;
            // Generate needs a selected source clip; upscale needs the task that produced the edit.
            const isUpscale = operation === AI_TOOLKIT_OPERATIONS.UPSCALE;
            if (isUpscale ? !completedTaskId : !formState.contentId) return;

            setShowSearch(false);
            dispatchVideoEditSubmit(
                isUpscale
                    ? { type: 'GENERATION_IN_PROGRESS', operation }
                    : {
                          type: 'GENERATION_IN_PROGRESS',
                          operation,
                          startTime: formState.startTime,
                          duration: formState.duration,
                          contentId: formState.contentId,
                      },
            );
            dispatchConfirmationModal({ type: 'CLOSE_MODAL' });
            setSubmitError(null);

            const request = isUpscale
                ? {
                      endpoint: UPSCALE_ENDPOINT,
                      body: {
                          taskType: 'upscale_video_edit',
                          parentTaskId: completedTaskId,
                          duration: editedDuration,
                      },
                      fallbackError: 'Upscale failed. Please try again.',
                      aiContentType: AiContentType.UPSCALE_VIDEO_EDIT,
                      aiModelName: UPSCALE_MODELS[0],
                  }
                : {
                      endpoint: VIDEO_EDIT_ENDPOINT,
                      body: {
                          contentId: formState.contentId,
                          prompt: formState.prompt.trim(),
                          startTime: formState.startTime,
                          duration: formState.duration,
                          algoParams: { modelId: formState.model },
                      },
                      fallbackError: 'Video edit failed. Please try again.',
                      aiContentType: AiContentType.AI_VIDEO_EDIT,
                      aiModelName: formState.model,
                  };

            try {
                const response = await fetch(request.endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(request.body),
                });
                void refreshCredits();

                const payload = (await response.json()) as AiTaskSubmitRouteResponse;

                if (!response.ok || payload.success !== true) {
                    const message =
                        ('message' in payload && payload.message) ||
                        ('error' in payload && payload.error) ||
                        request.fallbackError;
                    const failureReason = 'failureReason' in payload ? payload.failureReason ?? null : null;
                    setSubmitError(message);
                    dispatchVideoEditSubmit({ type: 'GENERATION_ERROR', operation, failureReason });
                    return;
                }

                dispatchVideoEditSubmit({
                    type: 'GENERATION_IN_PROGRESS',
                    // Repeated deliberately: the reducer defaults a bare submit to `generate`.
                    operation,
                    pendingTaskId: payload.taskId,
                    aiContentId: payload.aiContentId,
                    lastAiContentId: payload.lastAiContentId,
                    aiContentType: request.aiContentType,
                    aiModelName: request.aiModelName,
                });
            } catch (err) {
                void refreshCredits();
                setSubmitError(err instanceof Error ? err.message : request.fallbackError);
                dispatchVideoEditSubmit({ type: 'GENERATION_ERROR', operation });
            }
        },
        [
            completedTaskId,
            dispatchConfirmationModal,
            dispatchVideoEditSubmit,
            editedDuration,
            formState,
            isInProgress,
            refreshCredits,
            setShowSearch,
            setSubmitError,
        ],
    );

    return {
        formState,
        errors,
        promptState,
        promptHelperText,
        promptPlaceholder,
        suggestions,
        isValid,
        isSubmitDisabled,
        submitError,
        calculatedCost: VIDEO_EDIT_CREDIT_COST,
        onPromptChange,
        onPromptBlur,
        onUseCaseSelect,
        onSuggestionClick,
        onVideoSelect,
        onVideoDeselect,
        onSubmit,
        onConfirm,
    };
};
